import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../../components/ui/button';
import { CdaLogo } from '../../../components/cda/CdaBrand';
import { LanguageBar } from '../../../i18n/cda';
import { ArrowRight, Award, Heart, Users, Briefcase } from 'lucide-react';

const AboutDrGabrielaIbarraNoriega = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#fbf8f3]">
      <Helmet>
        <title>About Dr. Gabriela Ibarra-Noriega | Care Comply Founder</title>
        <meta name="description" content="Meet Dr. Gabriela Ibarra-Noriega, founder of Care Comply and passionate advocate for early childhood educators in Texas. Learn about her mission to help CDA candidates succeed." />
        <link rel="canonical" href="https://carecomplycda.com/about-dr-gabriela-ibarra-noriega" />
      </Helmet>

      <LanguageBar />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/cda"><CdaLogo size={34} /></Link>
          <Button onClick={() => navigate('/cda')} className="bg-[#0d7d6f] hover:bg-[#0a6358]">
            Start Your Journey
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-12">
        <div className="text-center mb-12">
          <p className="text-[#c4622d] font-semibold mb-2">Meet the Founder</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dr. Gabriela Ibarra-Noriega
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Founder of Care Comply and passionate advocate for early childhood educators in San Antonio and across Texas.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">My Story</h2>
          <p className="text-slate-600 mb-4">
            I've spent my career in early childhood education and workforce development, and I've seen firsthand how difficult it can be for aspiring educators to navigate the CDA credentialing process. Many talented individuals who love working with children struggle to complete their CDA — not because they lack ability, but because they lack guidance and support.
          </p>
          <p className="text-slate-600 mb-4">
            That's why I created Care Comply. I wanted to build a platform that walks CDA candidates through every step of their journey — from understanding the requirements to completing their 480 hours, building their portfolio, preparing for the observation, and ultimately launching their career.
          </p>
          <p className="text-slate-600 mb-4">
            Care Comply is more than just an app. It's a career coaching companion powered by Jazmin, an AI coach who understands the unique challenges faced by early childhood educators in Texas. Whether you're a first-generation college student, a working parent, or someone returning to the workforce, Care Comply is here to support you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Award className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Education Background</h3>
            <p className="text-slate-600">Doctoral degree with expertise in early childhood education and workforce development.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Heart className="w-10 h-10 text-[#c4622d] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Passion for Educators</h3>
            <p className="text-slate-600">Dedicated to removing barriers and creating pathways for aspiring childcare professionals.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Users className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Community Focus</h3>
            <p className="text-slate-600">Connecting CDA candidates with childcare centers and building a network of support in Texas.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Briefcase className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Barro Roots Contracting</h3>
            <p className="text-slate-600">Care Comply is a service of Barro Roots Contracting, supporting workforce development initiatives.</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">My Mission</h2>
          <p className="text-slate-600 mb-4">
            Every child deserves a qualified, caring educator. And every aspiring educator deserves the support they need to succeed. My mission with Care Comply is to:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#0d7d6f] text-white text-sm font-bold flex items-center justify-center shrink-0">1</span>
              <span className="text-slate-600"><strong>Guide candidates</strong> through every step of the CDA process with clear, actionable support</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#0d7d6f] text-white text-sm font-bold flex items-center justify-center shrink-0">2</span>
              <span className="text-slate-600"><strong>Connect candidates with centers</strong> to help them gain the experience they need</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#0d7d6f] text-white text-sm font-bold flex items-center justify-center shrink-0">3</span>
              <span className="text-slate-600"><strong>Build workforce readiness</strong> so candidates are prepared for long, successful careers</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#0d7d6f] text-white text-sm font-bold flex items-center justify-center shrink-0">4</span>
              <span className="text-slate-600"><strong>Serve Texas communities</strong> by strengthening the early childhood workforce</span>
            </li>
          </ul>
        </div>

        <div className="bg-[#0d7d6f] rounded-2xl p-8 text-white text-center mb-8">
          <h2 className="text-2xl font-bold mb-3">Ready to Start Your CDA Journey?</h2>
          <p className="mb-6 opacity-90">I created Care Comply for you. Let's get you that credential together.</p>
          <Button onClick={() => navigate('/cda')} className="bg-white text-[#0d7d6f] hover:bg-slate-100">
            Get Started Today <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <div className="text-center text-sm text-slate-500">
          <p>Care Comply serves San Antonio, Austin, Houston, Dallas, and all of Texas.</p>
          <p className="mt-2">
            <Link to="/cda-career-coaching-san-antonio" className="text-[#0d7d6f] hover:underline">Career Coaching</Link> • 
            <Link to="/cda-480-hours-support" className="text-[#0d7d6f] hover:underline ml-2">480-Hour Support</Link> • 
            <Link to="/cda-portfolio-help" className="text-[#0d7d6f] hover:underline ml-2">Portfolio Help</Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default AboutDrGabrielaIbarraNoriega;
